import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faChevronUp, faCopy } from '@fortawesome/pro-light-svg-icons';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { EditIcon } from 'src/app/core/ui/icons';
import { PlannerLink, PlannerLinkPlanner } from '../../planner-link.model';
import { PlannerSettings } from '../../../planner/planner.model';
import { LearnService } from '../../learn.service';
import { LearnStorageService } from '../../learn-storage.service';
import { LearnPlannerDayComponent } from '../learn-planner-day/learn-planner-day.component';

interface PlanMapCache {
  loaded: boolean;
  map: Map<string, any>;
}

@Component({
  selector: 'app-learn-planner',
  standalone: true,
  imports: [NgIf, NgFor, LearnPlannerDayComponent, FontAwesomeModule],
  templateUrl: './learn-planner.component.html',
  styleUrls: ['./learn-planner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LearnPlannerComponent {
  plannerLink = input.required<PlannerLink>();
  planner = input.required<PlannerLinkPlanner>();
  settings = input.required<PlannerSettings>();
  weeks = input.required<any>();

  icons = {
    closedIcon: faChevronDown,
    openedIcon: faChevronUp,
    editIcon: EditIcon,
    copyIcon: faCopy,
  };

  expanded = signal(false);

  private learnService = inject(LearnService);
  private learnStorageService = inject(LearnStorageService);
  private refresh$ = new BehaviorSubject<void>(undefined);
  private planMapCache = signal<PlanMapCache>({ loaded: false, map: new Map() });

  constructor() {
    // Set initial expanded state based on localStorage or planner count
    effect(() => {
      const plannerId = this.planner().planner_id;
      const storedState = this.learnStorageService.getPlannerState(plannerId);
      
      if (storedState !== null) {
        this.expanded.set(storedState);
      } else if (this.plannerLink().planners.length === 1) {
        this.expanded.set(true);
      }
    }, { allowSignalWrites: true });
  }

  toggle() {
    this.expanded.update(expanded => !expanded);
    
    // Save state to localStorage
    const plannerId = this.planner().planner_id;
    this.learnStorageService.setPlannerState(plannerId, this.expanded());
    
    if (!this.expanded()) {
      // Reset cache when closing
      this.planMapCache.set({ loaded: false, map: new Map() });
    }
  }

  private plans$ = combineLatest([
    toObservable(this.plannerLink),
    toObservable(this.planner),
    toObservable(this.weeks),
    toObservable(this.expanded),
    this.refresh$
  ]).pipe(
    filter(([plannerLink, planner, weeks, expanded]) => expanded && !!weeks?.length),
    // Debounce rapid changes
    debounceTime(300),
    map(([plannerLink, planner, weeks]) => ({
      planner_link_id: plannerLink.planner_link_id,
      planner_id: planner.planner_id,
      firstDay: weeks[0].days[0].toISOString().split('T')[0],
      lastDay: weeks[weeks.length - 1].days[weeks[weeks.length - 1].days.length - 1].toISOString().split('T')[0]
    })),
    // Only fetch if the date range or planner actually changed
    distinctUntilChanged((prev, curr) =>
      prev.planner_link_id === curr.planner_link_id &&
      prev.planner_id === curr.planner_id &&
      prev.firstDay === curr.firstDay &&
      prev.lastDay === curr.lastDay
    ),
    switchMap(({ planner_link_id, planner_id, firstDay, lastDay }) =>
      this.learnService.getPlanForDateRange$(
        planner_link_id,
        planner_id,
        firstDay,
        lastDay
      )
    ),
    map(plans => {
      const planMap = new Map();
      plans.forEach(plan => planMap.set(plan.date, plan));
      return planMap;
    })
  );

  private planMap = toSignal(this.plans$, { initialValue: new Map() });

  getPlan(date: string) {
    return this.planMap()?.get(date) || null;
  }

  refreshPlans() {
    this.refresh$.next();
  }

  trackByWeek = (index: number, object: any) => {
    return object.start;
  };

  trackByDay = (index: number, object: any) => {
    return index;
  };
}
